import { axiosJSONInstance } from '@/config';
import {
  ActivateWorkspaceResponse,
  InviteResult,
  Workspace,
  WorkspaceDeletionSummary,
  WorkspaceInvitation,
  WorkspaceRole,
} from '@/types';

// BOXY-44 P5 — the merchant's own workspaces and invitations.

export const fetchWorkspaces = async (): Promise<{ object: Workspace[] }> => {
  const response = await axiosJSONInstance.get('/merchants/workspaces');
  return response.data;
};

/**
 * Switch the active workspace.
 *
 * Returns a NEW token and invalidates the one used to call it. The caller MUST
 * persist the new token before making any other request — see useActivateWorkspace.
 */
export const activateWorkspace = async (
  uid: string,
): Promise<{ object: ActivateWorkspaceResponse }> => {
  const response = await axiosJSONInstance.post(`/merchants/workspaces/${uid}/activate`);
  return response.data;
};

/**
 * Rename ONE workspace, by uid — not whichever one the token addresses.
 *
 * `PATCH /merchants/profile/company` renames the ACTIVE workspace, so renaming
 * a second store from this page would have meant switching into it first: a new
 * token, a reload, and the owner left somewhere they did not ask to be.
 */
export const renameWorkspace = async (
  uid: string,
  name: string,
): Promise<{ object: Workspace }> => {
  const response = await axiosJSONInstance.patch(`/merchants/workspaces/${uid}`, { name });
  return response.data;
};

/** Everything the confirm modal shows, from the same predicate the delete uses. */
export const fetchDeletionSummary = async (
  uid: string,
): Promise<{ object: WorkspaceDeletionSummary }> => {
  const response = await axiosJSONInstance.get(`/merchants/workspaces/${uid}/deletion-summary`);
  return response.data;
};

export const deleteWorkspace = async (uid: string) => {
  const response = await axiosJSONInstance.delete(`/merchants/workspaces/${uid}`);
  return response.data;
};

export const fetchMyInvitations = async (): Promise<{ object: WorkspaceInvitation[] }> => {
  const response = await axiosJSONInstance.get('/merchants/invitations');
  return response.data;
};

export const acceptInvitation = async (uid: string) => {
  const response = await axiosJSONInstance.post(`/merchants/invitations/${uid}/accept`);
  return response.data;
};

export const declineInvitation = async (uid: string) => {
  const response = await axiosJSONInstance.post(`/merchants/invitations/${uid}/decline`);
  return response.data;
};

export interface CreateWorkspacePayload {
  name: string;
  support_phone?: string;
}

export const createWorkspace = async (
  payload: CreateWorkspacePayload,
): Promise<{ object: Workspace }> => {
  const response = await axiosJSONInstance.post('/merchants/workspaces', payload);
  return response.data;
};

/** A workspace OWNER/manager's view of what they have sent, spent ones included. */
export const fetchWorkspaceInvitations = async (
  workspaceUid: string,
): Promise<{ object: WorkspaceInvitation[] }> => {
  const response = await axiosJSONInstance.get(`/merchants/workspaces/${workspaceUid}/invitations`);
  return response.data;
};

/** The roles this workspace may offer an invitee — presets minus owner, plus its customs. */
export const fetchWorkspaceRoles = async (
  workspaceUid: string,
): Promise<{ object: WorkspaceRole[] }> => {
  const response = await axiosJSONInstance.get(`/merchants/workspaces/${workspaceUid}/roles`);
  return response.data;
};

/** The same payload Team Members sends, minus the fields a workspace invite has no use for. */
export interface InvitePayload {
  first_name: string;
  last_name: string;
  phone: string;
  role_uids: string[];
}

export const inviteToWorkspace = async (
  workspaceUid: string,
  payload: InvitePayload,
): Promise<{ object: InviteResult }> => {
  const response = await axiosJSONInstance.post(
    `/merchants/workspaces/${workspaceUid}/invitations`,
    payload,
  );

  return response.data;
};

export const revokeWorkspaceInvitation = async (workspaceUid: string, invitationUid: string) => {
  const response = await axiosJSONInstance.delete(
    `/merchants/workspaces/${workspaceUid}/invitations/${invitationUid}`,
  );
  return response.data;
};
